// Visual workflow builder. Operates on the existing workflows table — each
// workflow is a trigger + an ordered list of step nodes (sms / email / ai_call
// / stage / notify / wait). The sequenceRunner cron already executes these,
// so this is purely a better authoring surface.
import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Field, Input, Select, Modal, Spinner, Textarea } from '../ui.jsx';

const TRIGGERS = [
  { id:'new_lead',        label:'🆕 New lead created' },
  { id:'missed_call',     label:'📞 Missed call' },
  { id:'stage_qualified', label:'✓ Contact stage → Qualified' },
  { id:'stage_booked',    label:'✓ Contact stage → Booked' },
  { id:'stage_lost',      label:'✗ Contact stage → Lost' },
  { id:'estimate_sent',   label:'📄 Estimate sent' },
  { id:'invoice_paid',    label:'💰 Invoice paid' },
  { id:'job_completed',   label:'🔧 Job completed' },
  { id:'margin_drift',    label:'⚠ Job margin dropped below threshold' },
  { id:'New Lead',        label:'🆕 Stage = New Lead (legacy)' },
];

const STEP_TYPES = {
  text:    { label:'💬 Send SMS',           color:'#22d3ee', contentLabel:'Message body', placeholder:'Hi {first_name}, this is {business_name}…' },
  email:   { label:'📧 Send Email',         color:'#a78bfa', contentLabel:'Email body',   placeholder:'Subject is auto-set. Write the body.' },
  ai_call: { label:'📞 AI voice call',      color:'#22c55e', contentLabel:'Script name',  placeholder:'new_lead_qualifier, missed_call_follow_up, etc.' },
  stage:   { label:'🎯 Change pipeline stage', color:'#f97316', contentLabel:'New stage', placeholder:'Qualified, Booked, Lost…' },
  notify:  { label:'🔔 Notify the owner',   color:'#eab308', contentLabel:'What to say',  placeholder:'New hot lead waiting for callback' },
  wait:    { label:'⏰ Wait',               color:'#94a3b8', contentLabel:'Duration',     placeholder:'minutes' },
};

export function WorkflowBuilder({ user, toast }) {
  const [workflows, setWorkflows] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const load = () => api('/api/workflows').then(setWorkflows).catch(()=>setWorkflows([]));
  useEffect(() => { load(); }, []);

  const active = workflows.find(w => w.id === activeId);

  const create = async () => {
    const name = prompt('Workflow name (e.g. "Speed-to-lead — new lead")');
    if (!name?.trim()) return;
    try { const w = await api('/api/workflows', { method:'POST', body:{ name: name.trim(), trigger:'new_lead', status:'paused', steps:[] }});
      await load(); setActiveId(w.id); toast('Workflow created'); }
    catch (e) { toast(e.message, 'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>⚡ Workflow Builder</h1>
      <Btn v="primary" onClick={create}>+ New Workflow</Btn>
    </div>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18, lineHeight:1.6 }}>
      Automate everything that happens after a trigger fires. Stack SMS / Email / AI Voice / Stage Change / Wait / Notify steps in any order. The runner fires steps in sequence — wait steps pause until their duration elapses.
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:14 }}>
      <div style={{ ...styles.card, padding:8, alignSelf:'flex-start', position:'sticky', top:14 }}>
        <div style={{ padding:'6px 8px', fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700 }}>Workflows</div>
        {workflows.length === 0 && <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>None yet — create one →</div>}
        {workflows.map(w => (
          <div key={w.id} onClick={()=>setActiveId(w.id)} style={{
            padding:'10px 12px', borderRadius:8, cursor:'pointer',
            background: w.id === activeId ? G.purpleDim : 'transparent',
            borderLeft: `3px solid ${w.id === activeId ? G.purple : 'transparent'}`,
            marginBottom:2,
          }}>
            <div style={{ fontSize:13, fontWeight:600, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{w.name}</span>
              <Badge c={w.status==='active'?G.green:G.textMuted} bg={(w.status==='active'?G.green:G.textMuted)+'22'}>{w.status}</Badge>
            </div>
            <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{w.steps.length} step{w.steps.length===1?'':'s'} · {w.runs||0} runs</div>
          </div>
        ))}
      </div>

      {active ? <WorkflowEditor key={active.id} workflow={active} onChanged={load} toast={toast}/>
              : <div style={{ ...styles.card, padding:60, textAlign:'center', color:G.textMuted }}>Pick a workflow to edit, or create one.</div>}
    </div>
  </div>;
}

function WorkflowEditor({ workflow, onChanged, toast }) {
  const [name, setName] = useState(workflow.name);
  const [trigger, setTrigger] = useState(workflow.trigger);
  const [status, setStatus] = useState(workflow.status);
  const [steps, setSteps] = useState(workflow.steps);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setName(workflow.name); setTrigger(workflow.trigger); setStatus(workflow.status); setSteps(workflow.steps); setDirty(false);
  }, [workflow.id]);

  const markDirty = () => setDirty(true);
  const save = async () => {
    try { await api(`/api/workflows/${workflow.id}`, { method:'PATCH', body:{ name, trigger, status, steps }}); setDirty(false); onChanged(); toast('✓ Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const remove = async () => {
    if (workflow.protected) return toast('Protected workflow — cannot delete', 'error');
    if (!confirm(`Delete "${workflow.name}"?`)) return;
    try { await api(`/api/workflows/${workflow.id}`, { method:'DELETE' }); onChanged(); toast('Deleted'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const toggleStatus = () => { setStatus(status === 'active' ? 'paused' : 'active'); markDirty(); };

  const addStep = (type) => {
    const defaults = { wait: { delay_minutes: 60 }, text: {}, email: {}, ai_call: { content: 'new_lead_qualifier' } };
    setSteps([...steps, { type, content: '', ...(defaults[type] || {}) }]);
    setAdding(false); markDirty();
  };
  const updateStep = (i, patch) => { setSteps(steps.map((s, j) => j===i ? { ...s, ...patch } : s)); markDirty(); };
  const removeStep = (i) => { setSteps(steps.filter((_,j) => j!==i)); markDirty(); };
  const moveStep = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= steps.length) return;
    const next = [...steps]; [next[i], next[j]] = [next[j], next[i]]; setSteps(next); markDirty();
  };

  return <div>
    <div style={{ ...styles.card, padding:14, marginBottom:12 }}>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr auto auto auto', gap:10, alignItems:'flex-end' }}>
        <Field label="Workflow name"><Input value={name} onChange={e=>{ setName(e.target.value); markDirty(); }}/></Field>
        <Field label="Trigger">
          <Select value={trigger} onChange={e=>{ setTrigger(e.target.value); markDirty(); }}>
            {TRIGGERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </Select>
        </Field>
        <Btn sz="sm" v={status==='active'?'green':'ghost'} onClick={toggleStatus}>{status==='active'?'⏸ Pause':'▶ Activate'}</Btn>
        {!workflow.protected && <Btn sz="sm" v="danger" onClick={remove}>🗑</Btn>}
        <Btn sz="sm" v="primary" onClick={save} disabled={!dirty}>{dirty?'💾 Save':'Saved'}</Btn>
      </div>
    </div>

    {steps.length === 0 && <div style={{ ...styles.card, padding:40, textAlign:'center', color:G.textMuted, marginBottom:12 }}>
      No steps yet. Add one below to start the automation.
    </div>}

    {steps.map((s, i) => <StepCard key={i} step={s} index={i} count={steps.length} onUpdate={updateStep} onRemove={removeStep} onMove={moveStep}/>)}

    <div style={{ ...styles.card, padding:14, textAlign:'center', borderStyle:'dashed', borderColor:G.cardBorder }}>
      {adding ? (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center' }}>
          {Object.entries(STEP_TYPES).map(([type, meta]) => (
            <Btn key={type} sz="sm" onClick={()=>addStep(type)} style={{ borderColor: meta.color }}>{meta.label}</Btn>
          ))}
          <Btn sz="sm" v="ghost" onClick={()=>setAdding(false)}>Cancel</Btn>
        </div>
      ) : (
        <Btn v="primary" onClick={()=>setAdding(true)}>＋ Add step</Btn>
      )}
    </div>
  </div>;
}

function StepCard({ step, index, count, onUpdate, onRemove, onMove }) {
  const meta = STEP_TYPES[step.type] || { label: step.type, color: G.textMuted, contentLabel: 'Content' };
  return <div style={{ ...styles.card, padding:14, marginBottom:8, borderLeft:`4px solid ${meta.color}` }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <strong style={{ fontSize:13 }}>
        <span style={{ color:meta.color, fontFamily:'monospace', marginRight:8 }}>#{index+1}</span>
        {meta.label}
      </strong>
      <div style={{ display:'flex', gap:4 }}>
        <Btn sz="xs" disabled={index===0} onClick={()=>onMove(index, -1)}>↑</Btn>
        <Btn sz="xs" disabled={index===count-1} onClick={()=>onMove(index, +1)}>↓</Btn>
        <Btn sz="xs" v="danger" onClick={()=>onRemove(index)}>×</Btn>
      </div>
    </div>

    {step.type === 'wait' ? (
      <Field label="Wait duration (minutes)" hint="The workflow pauses here for this many minutes before running the next step. Use 60 = 1hr, 1440 = 1 day.">
        <Input type="number" min="1" value={step.delay_minutes || 60} onChange={e=>onUpdate(index, { delay_minutes: Number(e.target.value) })}/>
      </Field>
    ) : step.type === 'stage' ? (
      <Field label="New stage" hint="The contact's pipeline stage will be set to this value when the step runs.">
        <Select value={step.content||'Qualified'} onChange={e=>onUpdate(index, { content: e.target.value })}>
          {['New Lead','Contacted','Qualified','Estimate Sent','Booked','In Progress','Invoiced','Paid','Lost'].map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
    ) : (
      <Field label={meta.contentLabel} hint="Variables: {first_name} {last_name} {business_name} {phone} {service}">
        <Textarea value={step.content||''} onChange={e=>onUpdate(index, { content: e.target.value })} placeholder={meta.placeholder} rows={step.type==='email'?5:3}/>
      </Field>
    )}
  </div>;
}
